/**
 * FE-020 — a generated look (`GET /v1/product/{id}/outfits`) turned into canvas
 * pieces. The shopper does not own any of them: they are guests, drawn for this
 * canvas session and saved into the look as `{ productId }`, never as wardrobe
 * entries.
 */

import type { ProductOutfitItemV1, ProductOutfitV1 } from '@/types/ProductOutfitsV1Types'
import {
  fairStyleSpaceCatalogId,
  fairStyleSpaceCategoryForType,
  type FairStyleSpaceWardrobeItem,
} from './fair-style-space-wardrobe.data'

export function mapProductOutfitItemToGuest(
  item: ProductOutfitItemV1,
): FairStyleSpaceWardrobeItem {
  return {
    id: fairStyleSpaceCatalogId(item.product_id),
    name: item.name ?? '',
    category: fairStyleSpaceCategoryForType(item.type_id, { name: item.name }),
    imageSrc: item.image_url ?? '',
    source: 'catalog',
    productId: item.product_id,
    variantId: item.variant_id ?? null,
    typeId: item.type_id,
    brand: item.brand ?? null,
  }
}

/**
 * Pieces to add around the anchor. The anchor is already on the canvas — the
 * generator returns it inside `items`, so it is dropped here to avoid placing
 * the same product twice. Pieces without a picture are skipped: an empty slot
 * reads better than a blank tile.
 */
export function mapProductOutfitToCanvasGuests(
  outfit: ProductOutfitV1 | null | undefined,
  anchorProductId: number | null,
): FairStyleSpaceWardrobeItem[] {
  if (!outfit?.items?.length) return []
  const seen = new Set<number>()
  if (anchorProductId != null) seen.add(anchorProductId)
  const guests: FairStyleSpaceWardrobeItem[] = []
  for (const item of outfit.items) {
    if (item.product_id == null || seen.has(item.product_id)) continue
    if (!item.image_url) continue
    seen.add(item.product_id)
    guests.push(mapProductOutfitItemToGuest(item))
  }
  return guests
}

/**
 * FE-021 — the product card carries no `type_id`, only a name and breadcrumbs.
 * The generated look gives every piece a real type, so this guess is used **only**
 * as a fallback: when the generator fails and the canvas would otherwise open
 * empty after a button that promised a look.
 */
const CATEGORY_WORDS: ReadonlyArray<[RegExp, FairStyleSpaceWardrobeItem['category']]> = [
  [/\b(shoe|shoes|boot|boots|sneaker|sneakers|loafer|loafers|sandal|sandals|heel|heels|trainer|trainers)\b/i, 'shoes'],
  [/\b(bag|bags|tote|clutch|backpack|satchel|purse|pouch|handbag)\b/i, 'bags'],
  [/\b(coat|coats|jacket|jackets|blazer|blazers|parka|trench|puffer|cardigan)\b/i, 'outerwear'],
  [/\b(trouser|trousers|jean|jeans|skirt|skirts|short|shorts|pant|pants|legging|leggings)\b/i, 'bottoms'],
  [/\b(dress|dresses|shirt|shirts|top|tops|tee|t-shirt|sweater|sweaters|knit|blouse|jumper|hoodie)\b/i, 'tops'],
]

export function guessFairStyleSpaceCategory(
  ...parts: Array<string | null | undefined>
): FairStyleSpaceWardrobeItem['category'] {
  const text = parts.filter(Boolean).join(' ')
  for (const [pattern, category] of CATEGORY_WORDS) {
    if (pattern.test(text)) return category
  }
  return 'accessories'
}
